import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'app/hooks'

import { updateLicenseNoteThunk, updateLicenses } from 'features/licenses/slice'
import type { LicenseType } from 'features/licenses/types'
import NoteCell from './NoteCell'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'

interface NoteProps {
  original: LicenseType
  disableEdit?: boolean
}

function Note({
  original,
  disableEdit,
  original: { notes: initialNote },
}: NoteProps) {
  const dispatch = useAppDispatch()
  const rootDomainId = useAppSelector(currentRootDomainIdSelector)

  const [editMode, setEditMode] = useState(false)
  const [note, setNote] = useState<string>(initialNote || '')
  const [noteIsUpdating, setNoteIsUpdating] = useState(false)
  const [showError, setShowError] = useState(false)

  const licenseId = original?.id

  useEffect(() => {
    setNote(initialNote || '')
  }, [initialNote])

  useEffect(() => {
    setEditMode(false)
  }, [original.code])

  const handleEditModeClick = () => {
    setEditMode(!editMode)
  }

  const handleChange = (e: ChangeEvent<any>) => {
    setNote(e.target.value)
  }

  const isTransferred = useMemo(() => {
    const availableLicenseImported =
      !!original.domainId && original.domainId !== rootDomainId
    const activatedLicenseImported =
      !!original.homeRootDomainId && original.homeRootDomainId !== rootDomainId
    const youPurchasedLicense = original.purchaserDomainId === rootDomainId
    const transferred =
      (availableLicenseImported || activatedLicenseImported) &&
      youPurchasedLicense
    return transferred
  }, [
    original.domainId,
    original.homeRootDomainId,
    original.purchaserDomainId,
    rootDomainId,
  ])

  const handleSubmit = useCallback(async () => {
    if (rootDomainId && licenseId) {
      setNoteIsUpdating(true)
      try {
        const license = await dispatch(
          updateLicenseNoteThunk(rootDomainId, licenseId, note)
        )
        await dispatch(updateLicenses({ newLicense: license }))
      } catch (error) {
        setShowError(true)
        console.log(`Error updating License Note with id: ${licenseId}`, error)
      } finally {
        setNoteIsUpdating(false)
        setEditMode(false)
      }
    }
  }, [dispatch, rootDomainId, licenseId, note])

  const handleReject = () => {
    setNote(initialNote || '')
    setEditMode(false)
  }
  return (
    <NoteCell
      initialNote={initialNote}
      note={note}
      editMode={editMode}
      editDisabled={isTransferred || disableEdit}
      handleEditModeClick={handleEditModeClick}
      noteIsUpdating={noteIsUpdating}
      showError={showError}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      handleReject={handleReject}
      submitDisabled={!rootDomainId || !licenseId}
    />
  )
}

export default Note
